import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Check, ChevronRight, Loader2, Rocket, Users, Bot, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useOnboarding, type OnboardingStepKey } from '@/hooks/useOnboarding';
import { useProjects } from '@/hooks/useProjects';

interface OnboardingWizardV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type StepDef = {
  key: OnboardingStepKey;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
};

const STEPS: StepDef[] = [
  {
    key: 'create_project',
    icon: <Rocket className="h-7 w-7" aria-hidden="true" />,
    title: '¡Bienvenido a ProyectMind!',
    subtitle: 'Vamos a configurar tu espacio de trabajo en 3 pasos',
  },
  {
    key: 'invite_member',
    icon: <Users className="h-7 w-7" aria-hidden="true" />,
    title: 'Invita a tu equipo',
    subtitle: 'Puedes invitar hasta 5 personas en el plan gratuito',
  },
  {
    key: 'enable_ai',
    icon: <Bot className="h-7 w-7" aria-hidden="true" />,
    title: 'Activa tu IA',
    subtitle: 'VALEN analizará tus proyectos y sugerirá acciones',
  },
];

export const OnboardingWizardV2: React.FC<OnboardingWizardV2Props> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const { markStep, completeAll, dismiss } = useOnboarding();
  const { projects } = useProjects();

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const goNext = () => setStepIndex(i => Math.min(i + 1, STEPS.length - 1));
  const skip = () => {
    if (isLast) {
      dismiss();
      onOpenChange(false);
    } else {
      goNext();
    }
  };

  const handleCreateProject = () => {
    markStep('create_project');
    onOpenChange(false);
    navigate('/projects?new=1');
  };

  const handleSendInvite = async () => {
    const email = inviteEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Ingresa un correo electrónico válido');
      return;
    }
    const projectId = projects?.[0]?.id;
    if (!projectId) {
      toast.error('Crea primero un proyecto para invitar miembros');
      return;
    }
    setInviteLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: { email, projectId, role: 'miembro' },
      });
      if (error) throw error;
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      toast.success('¡Invitación enviada exitosamente!');
      setInviteEmail('');
      markStep('invite_member');
      goNext();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al enviar la invitación';
      toast.error(message);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleFinishAI = () => {
    if (aiEnabled) markStep('enable_ai');
    completeAll();
    onOpenChange(false);
    toast.success('¡Listo! Tu espacio de trabajo está configurado.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-0 shadow-2xl">
        {/* Stepper superior */}
        <div className="px-6 pt-6">
          <ol
            className="flex items-center justify-between gap-2 mb-6"
            aria-label="Pasos de configuración"
          >
            {STEPS.map((s, i) => {
              const isActive = i === stepIndex;
              const isDone = i < stepIndex;
              return (
                <li key={s.key} className="flex-1 flex items-center gap-2">
                  <div
                    className={[
                      'flex items-center justify-center h-8 w-8 rounded-full text-xs font-bold transition-colors',
                      isDone
                        ? 'bg-primary text-primary-foreground'
                        : isActive
                          ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                          : 'bg-muted text-muted-foreground',
                    ].join(' ')}
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={`Paso ${i + 1} de ${STEPS.length}`}
                  >
                    {isDone ? <Check className="h-4 w-4" aria-hidden="true" /> : i + 1}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 rounded ${isDone ? 'bg-primary' : 'bg-muted'}`}
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Header del paso */}
        <div className="px-6 pb-2 text-center">
          <div className="mx-auto mb-3 h-16 w-16 rounded-2xl pm-gradient-bg text-white flex items-center justify-center shadow-lg">
            {step.icon}
          </div>
          <h2 className="text-2xl font-bold text-foreground">{step.title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{step.subtitle}</p>
        </div>

        {/* Contenido del paso */}
        <div className="px-6 py-5 min-h-[140px] flex flex-col justify-center">
          {step.key === 'create_project' && (
            <p className="text-sm text-center text-muted-foreground">
              Empieza creando tu primer proyecto. Podrás configurar tareas, equipo y métricas en
              segundos.
            </p>
          )}

          {step.key === 'invite_member' && (
            <div className="space-y-3">
              <Label htmlFor="onboarding-invite-email">Correo del miembro</Label>
              <div className="flex gap-2">
                <Input
                  id="onboarding-invite-email"
                  type="email"
                  placeholder="persona@empresa.com"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  disabled={inviteLoading}
                />
                <Button onClick={handleSendInvite} disabled={inviteLoading || !inviteEmail.trim()}>
                  {inviteLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" aria-hidden="true" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                La invitación se asignará a tu proyecto más reciente.
              </p>
            </div>
          )}

          {step.key === 'enable_ai' && (
            <div className="space-y-4">
              <div className="rounded-xl border border-border bg-accent/40 p-4 flex items-start gap-3">
                <div className="h-9 w-9 rounded-full pm-gradient-bg text-white flex items-center justify-center flex-shrink-0">
                  <Bot className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-foreground">Hola, soy VALEN</p>
                  <p className="text-muted-foreground">
                    Voy a analizar tus proyectos y avisarte de riesgos, retrasos y oportunidades.
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
                <div className="text-sm">
                  <p className="font-medium text-foreground">Análisis predictivo</p>
                  <p className="text-xs text-muted-foreground">
                    Recomendaciones automáticas para cada proyecto
                  </p>
                </div>
                <Switch
                  checked={aiEnabled}
                  onCheckedChange={setAiEnabled}
                  aria-label="Activar análisis predictivo"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer con CTAs */}
        <div className="px-6 pb-6 pt-2 flex flex-col gap-2">
          {step.key === 'create_project' && (
            <Button onClick={handleCreateProject} className="w-full" size="lg">
              Crear mi primer proyecto
              <ChevronRight className="h-4 w-4 ml-1" aria-hidden="true" />
            </Button>
          )}

          {step.key === 'invite_member' && (
            <Button onClick={goNext} variant="default" className="w-full" size="lg">
              Continuar
              <ChevronRight className="h-4 w-4 ml-1" aria-hidden="true" />
            </Button>
          )}

          {step.key === 'enable_ai' && (
            <Button onClick={handleFinishAI} className="w-full" size="lg">
              ¡Empezar a usar ProyectMind!
              <ChevronRight className="h-4 w-4 ml-1" aria-hidden="true" />
            </Button>
          )}

          <button
            type="button"
            onClick={skip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Omitir por ahora
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizardV2;
